// CoachBlogPost.jsx - Artículo individual del blog de coach (Wayo Coach).
// Reutiliza Blog.css, usa el header de la landing de coach y CTA a /coach/register.
import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CoachLandingHeader from "../components/coach/CoachLandingHeader";
import Footer from "../components/Footer";
import { coachBlogPosts } from "../data/coachBlogPosts";
import { trackCoachCtaClick } from "../analytics/googleAnalytics";
import "./Blog.css";
import "./coach-blog.css";

const MID_ARTICLE_CTA = `
  <div class="cta-box">
    <h3>¿Todavía gestionas a tus clientes con Excel y WhatsApp?</h3>
    <p>Con <strong>Wayo Coach</strong> creas rutinas y dietas profesionales, haces seguimiento del progreso y das un servicio premium. Gratis, sin límite de clientes.</p>
    <p><a href="/coach/register" class="cta-button">Unirme gratis</a></p>
  </div>
`;

function injectMidArticleCta(content) {
  if (!content) return content;

  const withoutExistingCtas = content.replace(/\s*<div class="cta-box">[\s\S]*?<\/div>\s*/gi, "\n");
  const h2Matches = [...withoutExistingCtas.matchAll(/<h2\b[^>]*>/gi)];

  if (h2Matches.length >= 2) {
    const middleHeading = h2Matches[Math.floor(h2Matches.length / 2)];
    return `${withoutExistingCtas.slice(0, middleHeading.index)}${MID_ARTICLE_CTA}\n${withoutExistingCtas.slice(middleHeading.index)}`;
  }

  const lastWrapperClose = withoutExistingCtas.lastIndexOf("</div>");
  if (lastWrapperClose !== -1) {
    return `${withoutExistingCtas.slice(0, lastWrapperClose)}\n${MID_ARTICLE_CTA}\n${withoutExistingCtas.slice(lastWrapperClose)}`;
  }

  return `${withoutExistingCtas}\n${MID_ARTICLE_CTA}`;
}

export default function CoachBlogPost() {
  const { slug } = useParams();
  const post = coachBlogPosts.find((p) => p.slug === slug);
  const postContentWithMidCta = injectMidArticleCta(post?.content || "");

  if (!post) {
    return (
      <>
        <Helmet>
          <title>Artículo no encontrado | Wayo Coach Blog</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <main className="blog-page">
          <CoachLandingHeader />
          <div className="blog-container" style={{ paddingTop: "120px", textAlign: "center" }}>
            <h1>Artículo no encontrado</h1>
            <Link to="/blog-entrenadores" className="back-link">
              Volver al blog
            </Link>
          </div>
          <Footer />
        </main>
      </>
    );
  }

  const keywords = post.keywords || post.tags || [];
  // og:image / schema necesitan URL absoluta; los mockups son rutas locales.
  const ogImage = post.image?.startsWith("http")
    ? post.image
    : `https://wayoai.app${post.image}`;

  // Posts relacionados
  const relatedPosts = coachBlogPosts
    .filter(
      (p) =>
        p.id !== post.id &&
        (p.category === post.category || p.tags.some((tag) => post.tags.includes(tag)))
    )
    .slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{post.seoTitle || `${post.title} | Wayo Coach Blog`}</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={keywords.join(", ")} />
        <link rel="canonical" href={`https://wayoai.app/blog-entrenadores/${post.slug}`} />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="googlebot" content="index, follow, max-image-preview:large" />

        <meta property="og:title" content={post.seoTitle || post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://wayoai.app/blog-entrenadores/${post.slug}`} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        {post.tags.map((tag, index) => (
          <meta key={index} property="article:tag" content={tag} />
        ))}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.seoTitle || post.title,
            description: post.metaDescription,
            image: {
              "@type": "ImageObject",
              url: ogImage,
              width: 1200,
              height: 630,
            },
            datePublished: post.date,
            dateModified: post.dateModified || post.date,
            author: {
              "@type": "Organization",
              name: post.author || "Wayo Coach",
              url: "https://wayoai.app",
            },
            publisher: {
              "@type": "Organization",
              name: "Wayo",
              logo: {
                "@type": "ImageObject",
                url: "https://wayoai.app/logo.png",
              },
            },
            articleSection: post.category,
            keywords: keywords.join(", "),
            articleBody: post.content?.replace(/<[^>]*>/g, "") || "",
            wordCount: post.content?.replace(/<[^>]*>/g, "").split(/\s+/).length || 0,
            timeRequired: `PT${post.readTime?.split(" ")[0] || "10"}M`,
          })}
        </script>
        {post.faqSchema && (
          <script type="application/ld+json">{JSON.stringify(post.faqSchema)}</script>
        )}
      </Helmet>

      <main className="blog-page blog-post-page coach-blog-scope">
        <CoachLandingHeader />

        <article
          className="blog-post-content"
          itemScope
          itemType="https://schema.org/Article"
          itemProp="mainEntityOfPage"
        >
          <meta itemProp="headline" content={post.seoTitle || post.title} />
          <meta itemProp="description" content={post.metaDescription} />
          <meta itemProp="image" content={ogImage} />
          <meta itemProp="datePublished" content={post.date} />

          <div className="post-header">
            <div className="post-header-content">
              <Link to="/blog-entrenadores" className="back-link">
                Volver al blog
              </Link>

              <div className="post-meta-header">
                <span className="category-badge">{post.category}</span>
                <span className="post-date">
                  {new Date(post.date).toLocaleDateString("es-ES", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
                <span className="read-time">⏱️ {post.readTime} de lectura</span>
              </div>

              <h1 className="post-title-main">{post.h1 || post.title}</h1>

              <div className="post-author-info">
                <div className="author-avatar">{post.author.charAt(0)}</div>
                <div>
                  <p className="author-name">{post.author}</p>
                  <p className="author-role">Wayo Coach</p>
                </div>
              </div>
            </div>

            <div className="post-image-hero">
              <img src={post.image} alt={post.title} />
            </div>
          </div>

          <div className="post-container">
            <div className="post-main-content">
              <div
                className="post-body"
                dangerouslySetInnerHTML={{ __html: postContentWithMidCta }}
              />

              {/* CTA final: registro coach */}
              <div className="article-download-cta">
                <div className="article-download-copy">
                  <span className="article-download-badge">100% Gratis</span>
                  <h3>Empieza a gestionar a tus clientes con Wayo Coach</h3>
                  <p>
                    Crea rutinas y planes de nutrición con IA, haz seguimiento del progreso y da un
                    servicio premium. Sin cuota mensual, sin permanencia y sin límite de clientes.
                  </p>
                  <Link
                    to="/coach/register"
                    className="article-download-link"
                    onClick={() => trackCoachCtaClick(`coach_blogpost_cta_${post.slug}`)}
                  >
                    Unirme gratis
                  </Link>
                </div>
              </div>

              {/* Tags */}
              <div className="post-tags-section">
                <p className="tags-label">Etiquetas:</p>
                <div className="post-tags">
                  {post.tags.map((tag, index) => (
                    <Link
                      key={index}
                      to={`/blog-entrenadores?tag=${encodeURIComponent(tag)}`}
                      className="tag"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="post-sidebar">
              <div className="sidebar-sticky">
                <div className="sidebar-widget cta-widget">
                  <h3>🚀 Wayo Coach gratis</h3>
                  <p>La plataforma gratuita para entrenadores. Sin cuota, sin límite de clientes.</p>
                  <Link
                    to="/coach/register"
                    className="cta-btn"
                    onClick={() => trackCoachCtaClick(`coach_blogpost_sidebar_${post.slug}`)}
                  >
                    Unirme gratis
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          {/* Posts relacionados */}
          {relatedPosts.length > 0 && (
            <div className="related-posts-section">
              <div className="related-container">
                <h2 className="related-title">Artículos relacionados</h2>
                <div className="related-posts-grid">
                  {relatedPosts.map((relatedPost) => (
                    <Link
                      key={relatedPost.id}
                      to={`/blog-entrenadores/${relatedPost.slug}`}
                      className="related-post-card"
                    >
                      <div className="related-image">
                        <img src={relatedPost.image} alt={relatedPost.title} />
                      </div>
                      <div className="related-content">
                        <span className="category-badge">{relatedPost.category}</span>
                        <h3>{relatedPost.title}</h3>
                        <p className="read-time">⏱️ {relatedPost.readTime}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </article>

        <Footer />
      </main>
    </>
  );
}
